
import AddVehicle from './AddVehicle.vue';
import AutoServiceCheckbox from './AutoServiceCheckbox.vue';
import ErrorBanner from "Src/js/components/ErrorBanner.vue";
import StepControls from '../StepControls.vue';
import { AdditionalServiceResource, AdditionalServiceResourceType, AutomotiveSchedulerStep, IServiceInfoUpdateRequest, WorkOrderType } from 'JS/automotive/auto-service-scheduler/types/session';
import { BForm, BTextField } from 'JS/components/blain-form';
import { computed, defineComponent, nextTick, reactive,watch } from 'vue';
import { focusNextError } from 'Src/js/components/blain-form/errors';
import { useGlobalLoader } from 'JS/automotive/auto-service-scheduler/state/global-loader';
import { useOrderStore } from 'JS/automotive/auto-service-scheduler/state/order';
export default defineComponent({
	name: 'SelectServicesStep',
	components: {
		AddVehicle,
		AutoServiceCheckbox,
		BForm,
		BTextField,
		ErrorBanner,
		StepControls
	},
	emits: ['previous'],
	setup() {
		const orderStore = useOrderStore();
		const { loading } = useGlobalLoader();

		const serviceInfo = computed(() => orderStore.session?.serviceInfo);
		const servicesRequired = computed(() => orderStore.session.type === WorkOrderType.ServiceOnly);

		const hasVehicle = computed(() => !!orderStore.session?.workOrderSummary?.vehicleInfo);
		const canContinue = computed(() => !loading.value && hasVehicle.value);

		const errors = reactive({
			additionalCommentsError: '',
			requiredNotSelected: false,
			missingVehicle: false,
		});

		const formState = reactive<{
			services: AdditionalServiceResource[] | null,
			additionalComments: string,
		}>({
			services: null,
			additionalComments: ''
		});

		// once service info loads from session, setup state. stop watching so that tab change session refresh doesn't overwrite form
		const stopWatchingServiceInfo = watch(serviceInfo, (info) => {
			if (info == null) {
				return;
			}

			formState.services = info.additionalServices;
			formState.additionalComments = info.additionalComments;
			nextTick(() => stopWatchingServiceInfo());
		}, { immediate: true });

		async function updateServices() {
			if (loading.value) {
				return;
			}

			await orderStore.updateServices(getSelectedServiceNumbers());
			formState.services = orderStore.session.serviceInfo?.additionalServices;
		}

		async function handleSubmit() {
			if (loading.value) {
				return;
			}

			errors.missingVehicle = false;

			const request: IServiceInfoUpdateRequest = {
				additionalServiceBlainNumbers: getSelectedServiceNumbers(),
				additionalComments: formState.additionalComments
			};

			await orderStore.submitServiceInfo(request);
			if (orderStore.currentStep === AutomotiveSchedulerStep.Services && orderStore.session?.serviceInfo?.errors) {
				errors.additionalCommentsError = orderStore.session.serviceInfo.errors.additionalCommentsError;
				errors.requiredNotSelected = orderStore.session.serviceInfo.errors.missingRequiredService;
				errors.missingVehicle = orderStore.session.serviceInfo.errors.missingVehicle;
			}
			nextTick(() =>
				focusNextError()
			);
		}

		function getSelectedServiceNumbers() {
			const selectedServices = formState.services.filter(s => (s.isSelected));

			return selectedServices.map(s => s.type === AdditionalServiceResourceType.Single
				? s.blainNumber
				: s.selectedOptionBlainNumber
			);
		}

		function updateComments(comments: string) {
			formState.additionalComments = comments;
		}

		function updateSelection(name: string) {
			const service = formState.services.find(s => s.name == name);
			service.isSelected = !service.isSelected;
			if (service.type == AdditionalServiceResourceType.Multiple) {
				if (service.isSelected) {
					service.selectedOptionBlainNumber = service.options[0].blainNumber;
				} else {
					service.selectedOptionBlainNumber = '';
				}
			}
			updateServices();
			errors.additionalCommentsError = '';
			errors.requiredNotSelected = false;
		}

		function radioSelected(value: string, name: string) {
			const service = formState.services.find(s => s.name == name);
			// appease typescript
			if (service?.type === AdditionalServiceResourceType.Multiple) {
				service.selectedOptionBlainNumber = value;
			}
			updateServices();
		}


		return {
			step: AutomotiveSchedulerStep.Services,
			handleSubmit,
			servicesRequired,
			serviceInfo,
			formState,
			radioSelected,
			loading,
			canContinue,
			updateSelection,
			updateComments,
			hasVehicle,
			errors
		};
	}
});
